import { useEffect, useMemo, useState } from 'react';
import { Save, Search } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { ItemsTable } from '@/components/form/ItemsTable';
import { useToast } from '@/components/ui/useToast';
import {
  createRfq,
  updateRfq,
  type Rfq,
  type RfqItem,
} from '@/api/rfq';
import { listPrs, type Pr } from '@/api/pr';
import { listParties, type Party } from '@/api/parties';
import { listItems } from '@/api/items';
import { CategoryItemPicker } from '@/components/form/CategoryItemPicker';
import { apiError } from '@/lib/apiError';
import { todayISO } from '@/lib/format';

interface Props {
  open: boolean;
  editing: Rfq | null;
  onClose: () => void;
}

type FormState = {
  rfqDate: string;
  prId: number | '';
  deadline: string;
  remarks: string;
  vendorIds: number[];
  items: RfqItem[];
};

function emptyForm(): FormState {
  return {
    rfqDate: todayISO(),
    prId: '',
    deadline: '',
    remarks: '',
    vendorIds: [],
    items: [{ itemName: '', qty: '', unit: '', targetPrice: '' }],
  };
}

function fromRfq(r: Rfq): FormState {
  return {
    rfqDate: r.rfqDate.slice(0, 10),
    prId: r.prId ?? '',
    deadline: r.deadline ? r.deadline.slice(0, 10) : '',
    remarks: r.remarks ?? '',
    vendorIds: r.vendors.map((v) => v.vendor.id),
    items: r.items.length
      ? r.items.map((it) => ({
          itemId: it.itemId,
          itemName: it.itemName,
          qty: it.qty,
          unit: it.unit,
          targetPrice: it.targetPrice ?? '',
        }))
      : [{ itemName: '', qty: '', unit: '', targetPrice: '' }],
  };
}

export function RfqFormModal({ open, editing, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<FormState>(emptyForm);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? fromRfq(editing) : emptyForm());
  }, [open, editing]);

  const { data: prsResp } = useQuery({
    queryKey: ['pr', { status: 'Approved', noRfq: true }],
    queryFn: () => listPrs({ status: 'Approved', pageSize: 200, noRfq: true }),
    enabled: open,
    staleTime: 60_000,
  });
  const approvedPrs: Pr[] = useMemo(() => prsResp?.data ?? [], [prsResp]);

  const { data: vendorsResp } = useQuery({
    queryKey: ['parties', { type: 'vendor', pageSize: 10000 }],
    queryFn: () => listParties({ type: 'vendor', pageSize: 10000 }),
    enabled: open,
    staleTime: 60_000,
  });
  const vendors: Party[] = useMemo(() => vendorsResp?.data ?? [], [vendorsResp]);

  // Client-side search over the loaded vendor list. Matches name / city / GSTIN.
  const [vendorSearch, setVendorSearch] = useState('');
  useEffect(() => { if (!open) setVendorSearch(''); }, [open]);
  const filteredVendors = useMemo(() => {
    const q = vendorSearch.trim().toLowerCase();
    if (!q) return vendors;
    return vendors.filter((v) =>
      (v.name?.toLowerCase().includes(q)) ||
      (v.city?.toLowerCase().includes(q)) ||
      (v.gstin?.toLowerCase().includes(q))
    );
  }, [vendors, vendorSearch]);

  const { data: itemsResp } = useQuery({
    queryKey: ['items', { pageSize: 10000, status: 'Active' }],
    queryFn: () => listItems({ pageSize: 10000, status: 'Active' }),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const itemMaster = useMemo(() => itemsResp?.data ?? [], [itemsResp]);

  const editingId = editing?.id ?? null;
  const isReadOnly = editing && editing.status !== 'Draft';

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = toPayload(form);
      if (editingId) {
        return updateRfq(editingId, { ...payload, vendorIds: payload.vendorIds });
      }
      return createRfq(payload);
    },
    onSuccess: (rfq) => {
      qc.invalidateQueries({ queryKey: ['rfq'] });
      if (editingId) {
        toast.push(`RFQ ${rfq.rfqNo} updated`, 'success');
      } else {
        toast.pushStep(`RFQ ${rfq.rfqNo} created`, {
          body: 'Next step — send to vendors and wait for quotes.',
          href: '/purchase/rfq',
          nextLabel: 'Open RFQ list',
          tag: `rfq-created-${rfq.id}`,
        });
      }
      onClose();
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Save failed'), 'error');
    },
  });

  function onSubmit() {
    if (form.vendorIds.length === 0) {
      toast.push('Select at least one vendor', 'error');
      return;
    }
    const valid = form.items.filter((it) => it.itemName.trim() && Number(it.qty) > 0 && it.unit.trim());
    if (valid.length === 0) {
      toast.push('Add at least one valid item', 'error');
      return;
    }
    saveMutation.mutate();
  }

  function toggleVendor(id: number) {
    setForm((s) => ({
      ...s,
      vendorIds: s.vendorIds.includes(id) ? s.vendorIds.filter((x) => x !== id) : [...s.vendorIds, id],
    }));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editingId ? `Edit RFQ — ${editing?.rfqNo}` : 'New RFQ'}
      size="lg"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={saveMutation.isPending || !!isReadOnly}
          >
            {saveMutation.isPending ? 'Saving…' : (<span className="inline-flex items-center gap-1"><Save size={14} /> Save RFQ</span>)}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <FormGroup label="RFQ Number">
          <Input value={editing?.rfqNo ?? 'Auto'} readOnly className="opacity-70" />
        </FormGroup>
        <FormGroup label="Date" required>
          <Input
            type="date"
            value={form.rfqDate}
            onChange={(e) => setForm({ ...form, rfqDate: e.target.value })}
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="PR Reference">
          <Select
            value={String(form.prId)}
            onChange={(e) => {
              const v = e.target.value;
              const pr = approvedPrs.find((p) => String(p.id) === v);
              setForm({
                ...form,
                prId: v ? Number(v) : '',
                items:
                  pr && form.items.length === 1 && !form.items[0].itemName
                    ? pr.items.map((it) => ({
                        itemId: it.itemId,
                        itemName: it.itemName,
                        qty: it.qty,
                        unit: it.unit,
                        targetPrice: it.unitPrice ?? 0,
                      }))
                    : form.items,
              });
            }}
            disabled={!!isReadOnly}
          >
            <option value="">— None —</option>
            {approvedPrs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.prNo} — {p.department}
              </option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup label="Vendor Response Deadline">
          <Input
            type="date"
            value={form.deadline}
            onChange={(e) => setForm({ ...form, deadline: e.target.value })}
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Remarks" className="col-span-2">
          <Textarea
            rows={2}
            value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            disabled={!!isReadOnly}
          />
        </FormGroup>
      </div>

      {/* Vendor multi-select */}
      <div className="mt-4 rounded-card border border-[var(--border-color)] p-3.5">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-[11px] font-extrabold uppercase tracking-wide text-accent">
            Invite Vendors ({form.vendorIds.length} selected)
          </span>
          <div className="relative w-56 max-w-full">
            <Search size={12} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              type="text"
              value={vendorSearch}
              onChange={(e) => setVendorSearch(e.target.value)}
              placeholder="Search vendors…"
              className="w-full rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] py-1.5 pl-7 pr-2 text-[11px] text-[var(--text-primary)] outline-none focus:border-accent"
            />
          </div>
        </div>
        <div className="grid max-h-56 grid-cols-2 gap-1.5 overflow-y-auto pr-1">
          {filteredVendors.map((v) => {
            const checked = form.vendorIds.includes(v.id);
            return (
              <label
                key={v.id}
                className={`flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
                  checked
                    ? 'border-accent/40 bg-accent/10 text-[var(--text-primary)]'
                    : 'border-[var(--border-color)] bg-white/[0.02] text-[var(--text-secondary)] hover:border-accent/30'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleVendor(v.id)}
                  disabled={!!isReadOnly}
                />
                <span className="flex-1 truncate font-bold">{v.name}</span>
                <span className="text-[10px] text-[var(--text-muted)]">{v.city ?? ''}</span>
              </label>
            );
          })}
          {vendors.length === 0 && (
            <div className="col-span-2 py-3 text-center text-xs text-[var(--text-muted)]">
              No vendors in Party Master — add some first under /masters/parties
            </div>
          )}
          {vendors.length > 0 && filteredVendors.length === 0 && (
            <div className="col-span-2 py-3 text-center text-xs text-[var(--text-muted)]">
              No vendors match "{vendorSearch}"
            </div>
          )}
        </div>
      </div>

      <ItemsTable<RfqItem>
        title="Items to Quote"
        rows={form.items}
        onChange={(items) => setForm({ ...form, items })}
        newRow={() => ({ itemName: '', qty: '', unit: '', targetPrice: '' })}
        columns={[
          {
            key: 'item',
            header: 'Item',
            cell: (row, _i, update) => (
              <CategoryItemPicker
                items={itemMaster.map((it) => ({
                  id: it.id,
                  code: it.code,
                  name: it.name,
                  category: it.category,
                  currentStock: 0,
                }))}
                value={row.itemId ?? '__custom__'}
                disabled={!!isReadOnly}
                onCustom={() => update({ itemId: null })}
                onPick={(id) => {
                  const it = itemMaster.find((i) => i.id === id);
                  update({
                    itemId: id,
                    itemName: it?.name ?? row.itemName,
                    unit: it?.unit ?? row.unit,
                    targetPrice: it ? Number(it.purchaseRate || 0) : row.targetPrice,
                  });
                }}
              />
            ),
          },
          {
            key: 'itemName',
            header: 'Description',
            cell: (row, _i, update) => (
              <Input
                value={row.itemName}
                onChange={(e) => update({ itemName: e.target.value })}
                disabled={!!isReadOnly}
              />
            ),
          },
          {
            key: 'qty',
            header: 'Qty',
            width: '90px',
            align: 'right',
            cell: (row, _i, update) => (
              <Input
                type="number"
                value={row.qty as string}
                onChange={(e) => update({ qty: e.target.value })}
                disabled={!!isReadOnly}
                className="text-right"
              />
            ),
          },
          {
            key: 'unit',
            header: 'Unit',
            width: '110px',
            cell: (row, _i, update) => (
              <Input
                value={row.unit}
                onChange={(e) => update({ unit: e.target.value })}
                disabled={!!isReadOnly}
              />
            ),
          },
          {
            key: 'targetPrice',
            header: 'Target Price',
            width: '120px',
            align: 'right',
            cell: (row, _i, update) => (
              <Input
                type="number"
                step="0.01"
                value={String(row.targetPrice ?? '')}
                onChange={(e) => update({ targetPrice: Number(e.target.value) })}
                disabled={!!isReadOnly}
                className="text-right"
              />
            ),
          },
        ]}
      />
    </Modal>
  );
}

function toPayload(f: FormState) {
  return {
    rfqDate: f.rfqDate,
    prId: typeof f.prId === 'number' ? f.prId : undefined,
    deadline: f.deadline || undefined,
    remarks: f.remarks.trim() || undefined,
    vendorIds: f.vendorIds,
    items: f.items
      .filter((it) => it.itemName.trim() && Number(it.qty) > 0 && it.unit.trim())
      .map((it) => ({
        itemId: it.itemId ?? undefined,
        itemName: it.itemName.trim(),
        qty: Number(it.qty),
        unit: it.unit.trim(),
        targetPrice: it.targetPrice != null && it.targetPrice !== '' ? Number(it.targetPrice) : undefined,
      })),
  };
}
